import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/solid-query';
import { useAuth } from './AuthContext';
import type { BoxCreateRequest, Page, SshCreateRequest, StackCreateRequest } from './types';

export const queryKeys = {
  boxes: (uid: string) => ['boxes', uid] as const,
  box: (uid: string, boxId: string) => ['box', uid, boxId] as const,
  boxPassword: (uid: string, boxId: string) => ['box-password', uid, boxId] as const,
  boxStacks: (uid: string, boxId: string) => ['box-stacks', uid, boxId] as const,
  boxEvents: (uid: string, boxId: string) => ['box-events', uid, boxId] as const,
  sshKeys: (uid: string) => ['ssh-keys', uid] as const,
  stacks: (includeNonManifest = false) => ['stacks', includeNonManifest] as const,
};

/** While anything is provisioning or being torn down the list is re-read every
 *  5s; otherwise it only refreshes on demand. */
const UPDATING_POLL_MS = 5_000;

/** Poll until the queued job is observed as Updating, or the startup grace expires. */
const JOB_START_GRACE_MS = 30_000;

const watchUntil = new Map<string, Map<string, number>>();

/** Call when a background job has been queued. */
function watchBoxActivity(uid: string, boxId: string): void {
  const boxes = watchUntil.get(uid) ?? new Map<string, number>();
  boxes.set(boxId, Date.now() + JOB_START_GRACE_MS);
  watchUntil.set(uid, boxes);
}

function pollWhile(uid: string, busy: boolean, boxId?: string): number | false {
  const boxes = watchUntil.get(uid);
  if (busy && boxId !== undefined) boxes?.delete(boxId);
  const now = Date.now();
  for (const [id, deadline] of boxes ?? []) {
    if (deadline <= now) boxes?.delete(id);
  }
  if (boxes?.size === 0) watchUntil.delete(uid);
  const awaitingStart = boxId === undefined ? !!boxes?.size : boxes?.has(boxId);
  return busy || awaitingStart ? UPDATING_POLL_MS : false;
}

export function flatten<T>(pages: Page<T>[] | undefined): T[] {
  return (pages ?? []).flatMap((page) => page.items);
}

export function useBoxes() {
  const auth = useAuth();
  return useInfiniteQuery(() => ({
    queryKey: queryKeys.boxes(auth.uid()),
    enabled: auth.status() === 'authenticated',
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }: { pageParam: string | null }) =>
      auth.api.listBoxes(auth.uid(), { ec2Status: true, next: pageParam }),
    getNextPageParam: (lastPage) => lastPage.next ?? undefined,
    refetchInterval: (query) => {
      const boxes = flatten(query.state.data?.pages);
      const updating = boxes.filter((box) => box.box_status === 'Updating');
      for (const box of updating) watchUntil.get(auth.uid())?.delete(box.box_id);
      return pollWhile(auth.uid(), updating.length > 0);
    },
  }));
}

export function useBox(boxId: () => string) {
  const auth = useAuth();
  return useQuery(() => ({
    queryKey: queryKeys.box(auth.uid(), boxId()),
    enabled: auth.status() === 'authenticated' && boxId() !== '',
    queryFn: () => auth.api.getBox(auth.uid(), boxId()),
    refetchInterval: (query) => pollWhile(auth.uid(), query.state.data?.box_status === 'Updating', boxId()),
  }));
}

/** Fetched only on request; gcTime 0 drops the password from the cache once
 *  nothing displays it. */
export function useBoxPassword(boxId: () => string, enabled: () => boolean) {
  const auth = useAuth();
  return useQuery(() => ({
    queryKey: queryKeys.boxPassword(auth.uid(), boxId()),
    enabled: auth.status() === 'authenticated' && enabled(),
    queryFn: () => auth.api.getBoxPassword(auth.uid(), boxId()),
    gcTime: 0,
  }));
}

export function useBoxStacks(boxId: () => string, enabled: () => boolean, busy: () => boolean = () => false) {
  const auth = useAuth();
  return useInfiniteQuery(() => ({
    queryKey: queryKeys.boxStacks(auth.uid(), boxId()),
    enabled: auth.status() === 'authenticated' && enabled(),
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }: { pageParam: string | null }) =>
      auth.api.listBoxStacks(auth.uid(), boxId(), pageParam),
    getNextPageParam: (lastPage) => lastPage.next ?? undefined,
    // A deploying job records the association before it starts installing
    // (`deploy_box_stack`, rust_code/box_jobs/src/deploy_stack/mod.rs), so the stack shows up here
    // while the box is still working.
    refetchInterval: () => pollWhile(auth.uid(), busy(), boxId()),
  }));
}

/** A failed job reports itself only here, so the feed is re-read while the box
 *  is working — that is when an error is about to appear. */
export function useBoxEvents(boxId: () => string, busy: () => boolean) {
  const auth = useAuth();
  return useInfiniteQuery(() => ({
    queryKey: queryKeys.boxEvents(auth.uid(), boxId()),
    enabled: auth.status() === 'authenticated',
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }: { pageParam: string | null }) =>
      auth.api.listBoxEvents(auth.uid(), boxId(), pageParam),
    getNextPageParam: (lastPage) => lastPage.next ?? undefined,
    refetchInterval: () => pollWhile(auth.uid(), busy(), boxId()),
  }));
}

export function useSshKeys() {
  const auth = useAuth();
  return useInfiniteQuery(() => ({
    queryKey: queryKeys.sshKeys(auth.uid()),
    enabled: auth.status() === 'authenticated',
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }: { pageParam: string | null }) => auth.api.listSshKeys(auth.uid(), pageParam),
    getNextPageParam: (lastPage) => lastPage.next ?? undefined,
  }));
}

/** `includeNonManifest` also lists S3 prefixes whose manifest is missing or
 *  unreadable — the administration view shows them so they can be fixed. */
export function useStacks(includeNonManifest = false) {
  const auth = useAuth();
  return useInfiniteQuery(() => ({
    queryKey: queryKeys.stacks(includeNonManifest),
    enabled: auth.status() === 'authenticated',
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }: { pageParam: string | null }) => auth.api.listStacks(pageParam, includeNonManifest),
    getNextPageParam: (lastPage) => lastPage.next ?? undefined,
  }));
}

export function useCreateBox() {
  const auth = useAuth();
  const client = useQueryClient();
  return useMutation(() => ({
    mutationFn: (request: BoxCreateRequest) => auth.api.createBox(auth.uid(), request),
    onSuccess: (result) => {
      watchBoxActivity(auth.uid(), result.box_id);
      return client.invalidateQueries({ queryKey: queryKeys.boxes(auth.uid()) });
    },
  }));
}

export function useDestroyBox() {
  const auth = useAuth();
  const client = useQueryClient();
  return useMutation(() => ({
    mutationFn: (boxId: string) => auth.api.destroyBox(auth.uid(), boxId),
    onSuccess: (_result, boxId) => {
      watchBoxActivity(auth.uid(), boxId);
      return Promise.all([
        client.invalidateQueries({ queryKey: queryKeys.boxes(auth.uid()) }),
        client.invalidateQueries({ queryKey: queryKeys.box(auth.uid(), boxId) }),
      ]);
    },
  }));
}

/** The request only queues the job. Nothing is deployed yet when this resolves,
 *  so the caller gets polling rather than a result: the box list carries the
 *  Updating status, the stack list gains the stack once the job writes it, and
 *  the event feed carries the failure if there is one. */
export function useDeployStack() {
  const auth = useAuth();
  const client = useQueryClient();
  return useMutation(() => ({
    mutationFn: (variables: { boxId: string; stackIds: string[] }) =>
      auth.api.deployStacks(auth.uid(), variables.boxId, variables.stackIds),
    onSuccess: (_result, variables) => {
      watchBoxActivity(auth.uid(), variables.boxId);
      return Promise.all([
        client.invalidateQueries({ queryKey: queryKeys.boxes(auth.uid()) }),
        client.invalidateQueries({ queryKey: queryKeys.box(auth.uid(), variables.boxId) }),
        client.invalidateQueries({ queryKey: queryKeys.boxStacks(auth.uid(), variables.boxId) }),
        client.invalidateQueries({ queryKey: queryKeys.boxEvents(auth.uid(), variables.boxId) }),
      ]);
    },
  }));
}

export function useCreateSshKey() {
  const auth = useAuth();
  const client = useQueryClient();
  return useMutation(() => ({
    mutationFn: (request: SshCreateRequest) => auth.api.createSshKey(auth.uid(), request),
    onSuccess: () => client.invalidateQueries({ queryKey: queryKeys.sshKeys(auth.uid()) }),
  }));
}

export function useCreateStack() {
  const auth = useAuth();
  const client = useQueryClient();
  return useMutation(() => ({
    mutationFn: (request: StackCreateRequest) => auth.api.createStack(request),
    // Prefix key: refreshes the catalog and the administration list alike.
    onSuccess: () => client.invalidateQueries({ queryKey: ['stacks'] }),
  }));
}
